import {
  Component,
  DOCUMENT,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import type { OnDestroy } from '@angular/core';

import { Button } from '../button';
import { Divider } from '../divider';
import { Icon } from '../icon';
import { drawerAnimations } from './drawer.animations';
import type { DrawerPosition, DrawerSize } from './drawer.types';
import { DRAWER_POSITION_CLASSES, DRAWER_SIZE_CLASSES } from './drawer.types';

/**
 * @deprecated Use DialogService instead
 */
@Component({
  selector: 'tvd-drawer',

  imports: [Button, Icon, Divider],
  animations: drawerAnimations,
  template: `
    <!-- Backdrop -->
    @if (hasBackdrop()) {
      <div
        class="fixed inset-0 bg-black/50 z-[10001]"
        [@backdropFade]="backdropState()"
        (click)="onBackdropClick()"
      ></div>
    }

    <!-- Drawer Container -->
    <div
      #drawerContainer
      class="fixed top-0 h-full z-[10002] bg-card shadow-xl w-full border-l border-card-border flex flex-col"
      [class]="drawerClasses()"
      [@slideInFromRight]="drawerState()"
      (keydown.escape)="onEscapeKey()"
    >
      <!-- Header -->
      @if (showHeader()) {
        <div class="shrink-0 px-6 -mx-6">
          <div
            class="flex items-center justify-between py-4 px-4"
            [class.shadow-md]="headerShadow()"
          >
            <div class="flex items-center">
              <h2 class="text-lg font-semibold ">{{ title() }}</h2>
            </div>
            <button tvdButton kind="secondary" (click)="close()">
              <tvd-icon size="lg" icon="close" />
            </button>
          </div>
          <tvd-divider />
        </div>
      }

      <!-- Content -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <div class="px-4" [class.py-4]="outletSpacing()">
          <ng-content />
        </div>
      </div>

      <!-- Footer -->
      @if (showFooter()) {
        <div class="flex-shrink-0 bottom-padding-safe">
          <ng-content select="[slot=footer]" />
        </div>
      }
    </div>
  `,
  host: {
    '[class.pointer-events-none]': '!isOpen()',
    '[class.pointer-events-auto]': 'isOpen()',
  },
})
export class DrawerComponent implements OnDestroy {
  readonly #document = inject(DOCUMENT);
  readonly #elementRef = inject(ElementRef);

  // Inputs
  readonly isOpen = model<boolean>(false);
  readonly title = input<string>('');
  readonly size = input<DrawerSize>('medium');
  readonly position = input<DrawerPosition>('right');
  readonly hasBackdrop = input<boolean>(true);
  readonly closeOnBackdropClick = input<boolean>(true);
  readonly closeOnEscape = input<boolean>(true);
  readonly showHeader = input<boolean>(true);
  readonly showFooter = input<boolean>(false);
  readonly outletSpacing = input<boolean>(true);
  readonly headerShadow = input<boolean>(true);

  // Outputs
  readonly opened = output<void>();
  readonly closed = output<void>();

  // View children
  readonly drawerContainer =
    viewChild<ElementRef<HTMLElement>>('drawerContainer');

  // Computed properties
  readonly drawerClasses = computed(() => {
    const sizeClass = DRAWER_SIZE_CLASSES[this.size()];
    const positionClass = DRAWER_POSITION_CLASSES[this.position()];
    return `${sizeClass} ${positionClass}`;
  });

  readonly drawerState = computed(() => (this.isOpen() ? 'open' : 'closed'));
  readonly backdropState = computed(() =>
    this.isOpen() ? 'visible' : 'hidden',
  );

  // Focus management
  readonly #previouslyFocusedElement = signal<HTMLElement | null>(null);

  constructor() {
    // Handle focus management when drawer opens/closes
    effect(() => {
      if (this.isOpen()) {
        this.#previouslyFocusedElement.set(
          this.#document.activeElement as HTMLElement,
        );
        this.#focusDrawer();
        this.opened.emit();
      } else {
        this.#restoreFocus();
        this.closed.emit();
      }
    });
  }

  ngOnDestroy(): void {
    this.#restoreFocus();
  }

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen.set(!this.isOpen());
  }

  onBackdropClick(): void {
    if (this.closeOnBackdropClick()) {
      this.close();
    }
  }

  onEscapeKey(): void {
    if (this.closeOnEscape()) {
      this.close();
    }
  }

  #focusDrawer(): void {
    // Focus the drawer container after animation
    setTimeout(() => {
      const drawer = this.drawerContainer();
      if (drawer) {
        drawer.nativeElement.focus();
      }
    }, 300);
  }

  #restoreFocus(): void {
    const previousElement = this.#previouslyFocusedElement();
    if (previousElement && typeof previousElement.focus === 'function') {
      previousElement.focus();
    }
  }
}
